import React, {
  useState,
  useMemo,
  useCallback,
  useRef,
  useEffect,
} from "react";
import {
  Container,
  Table,
  Row,
  Col,
  Form,
  Pagination,
  Card,
  Alert,
  Button,
} from "react-bootstrap";
import {
  FaChevronUp,
  FaChevronDown,
  FaSortUp,
  FaSortDown,
  FaDownload,
  FaQuestionCircle,
} from "react-icons/fa";
import html2canvas from "html2canvas";
import { useGetGameDataQuery } from "../slices/gameDataApiSlice";
import { useGetShotsByGameIdQuery } from "../slices/shotDataApiSlice";
import Loader from "../components/Loader";
import Message from "../components/Message";
import ShotMapWithFilters from "../components/ShotMapWithFilters";
import ScreenTabs from "../components/ScreenTabs";

const ANALYSIS_TABS = [
  { label: "Game Analysis", to: "/ShotAnalysisScreen" },
  { label: "Player Analysis", to: "/PlayerAnalysisScreen" },
  { label: "Season Analysis", to: "/SeasonAnalysisScreen" },
];

const formatTooltipTime = (secondsValue) => {
  const totalSeconds = Number(secondsValue);
  if (!Number.isFinite(totalSeconds)) return "00:00:00";
  const safeSeconds = Math.max(0, Math.floor(totalSeconds));
  const hours = String(Math.floor(safeSeconds / 3600)).padStart(2, "0");
  const minutes = String(Math.floor((safeSeconds % 3600) / 60)).padStart(
    2,
    "0",
  );
  const seconds = String(safeSeconds % 60).padStart(2, "0");
  return `${hours}:${minutes}:${seconds}`;
};

const PERFORMANCE_CHART_WIDTH = 560;
const PERFORMANCE_CHART_HEIGHT = 400;
const PERFORMANCE_CHART_MARGIN = {
  top: 44,
  right: 18,
  bottom: 56,
  left: 58,
};
const toNumericCell = (cell) => {
  if (typeof cell === "object" && cell !== null) {
    return Number(cell.v || 0);
  }
  return Number(cell || 0);
};

const toHalfRelativeSeconds = (gameHalf, gameTimeSeconds) => {
  return Number(gameTimeSeconds) || 0;
};

const buildPerformanceChartModel = (rows, xMax) => {
  const lineSeries = [[], [], [], []];
  const pointSeries = [[], [], [], []];

  if (!rows.length || rows.length === 1) {
    const emptyLine = [
      { x: 0, y: 0 },
      { x: xMax, y: 0 },
    ];
    return {
      lineSeries: lineSeries.map(() => [...emptyLine]),
      pointSeries,
      yMax: 1,
    };
  }

  rows.slice(1).forEach((row, rowIndex) => {
    const x = Number(row[0] || 0);
    const lineValues = [row[1], row[3], row[5], row[7]];
    const pointDefs = [
      { valueIndex: 9, tooltipIndex: 10 },
      { valueIndex: 11, tooltipIndex: 12 },
      { valueIndex: 13, tooltipIndex: 14 },
      { valueIndex: 15, tooltipIndex: 16 },
    ];

    lineValues.forEach((cell, seriesIndex) => {
      lineSeries[seriesIndex].push({
        x,
        y: toNumericCell(cell),
      });
    });

    pointDefs.forEach(({ valueIndex, tooltipIndex }, seriesIndex) => {
      if (row[valueIndex] === null || row[valueIndex] === undefined) return;
      pointSeries[seriesIndex].push({
        key: `${rowIndex}-${seriesIndex}-${x}`,
        x,
        y: toNumericCell(row[valueIndex]),
        tooltip: String(row[tooltipIndex] || ""),
      });
    });
  });

  const yValues = [
    ...lineSeries.flat().map((point) => point.y),
    ...pointSeries.flat().map((point) => point.y),
  ];
  const yMax = Math.max(1, Math.ceil(Math.max(...yValues, 0)));

  return {
    lineSeries,
    pointSeries,
    yMax,
  };
};

function PerformanceChart({
  title,
  rows,
  hasData,
  homeColor,
  awayColor,
  hoverState,
  setHoverState,
  halfDurationMinutes,
  legend,
}) {
  const chartModel = useMemo(
    () => buildPerformanceChartModel(rows, halfDurationMinutes),
    [rows, halfDurationMinutes],
  );

  const width = PERFORMANCE_CHART_WIDTH;
  const height = PERFORMANCE_CHART_HEIGHT;
  const { top, right, bottom, left } = PERFORMANCE_CHART_MARGIN;
  const legendWidth = 96;
  const plotRight = right + legendWidth;
  const plotWidth = width - left - plotRight;
  const plotHeight = height - top - bottom;
  const xScale = (value) =>
    left +
    (Math.max(0, Math.min(halfDurationMinutes, value)) / halfDurationMinutes) *
      plotWidth;
  const yScale = (value) =>
    top +
    plotHeight -
    (Math.max(0, Math.min(chartModel.yMax, value)) / chartModel.yMax) *
      plotHeight;

  const xTicks = [0, 10, 20, 30];
  if (!xTicks.includes(halfDurationMinutes)) xTicks.push(halfDurationMinutes);

  const tickStep = Math.max(1, Math.ceil(chartModel.yMax / 6));
  const yTicks = [];
  for (let value = 0; value <= chartModel.yMax; value += tickStep) {
    yTicks.push(value);
  }
  if (yTicks[yTicks.length - 1] !== chartModel.yMax)
    yTicks.push(chartModel.yMax);

  const seriesStyles = [
    { color: homeColor, dash: "" },
    { color: homeColor, dash: "6 4" },
    { color: awayColor, dash: "" },
    { color: awayColor, dash: "6 4" },
  ];

  const linePath = (points) =>
    points
      .map(
        (point, index) =>
          `${index === 0 ? "M" : "L"} ${xScale(point.x)} ${yScale(point.y)}`,
      )
      .join(" ");

  return (
    <div style={{ position: "relative", background: "#fff", borderRadius: 6 }}>
      {hoverState?.text ? (
        <div
          style={{
            position: "absolute",
            left: `${hoverState.xPct}%`,
            top: `${hoverState.yPct}%`,
            transform: hoverState.anchorRight
              ? "translate(calc(-100% - 10px), -50%)"
              : "translate(10px, -50%)",
            zIndex: 3,
            background: "rgba(255,255,255,0.96)",
            color: "#111",
            border: "1px solid rgba(0,0,0,0.2)",
            borderRadius: 6,
            padding: "8px 10px",
            whiteSpace: "pre-line",
            fontSize: 12,
            maxWidth: 220,
            pointerEvents: "none",
          }}
        >
          {hoverState.text}
        </div>
      ) : null}
      {legend ? (
        <div
          style={{
            position: "absolute",
            right: 6,
            top: "50%",
            transform: "translateY(-50%)",
            zIndex: 2,
          }}
        >
          {legend}
        </div>
      ) : null}
      <svg
        viewBox={`0 0 ${width} ${height}`}
        style={{
          width: "100%",
          height: "400px",
          display: "block",
          borderRadius: 6,
        }}
        role="img"
        aria-label={title}
      >
        <rect
          x="0"
          y="0"
          width={width}
          height={height}
          fill="#ffffff"
          rx="6"
          ry="6"
        />
        <text
          x={width / 2}
          y="24"
          textAnchor="middle"
          fontSize="16"
          fontWeight="700"
          fill="#111"
        >
          {title}
        </text>

        {yTicks.map((tick) => (
          <g key={`y-${tick}`}>
            <line
              x1={left}
              y1={yScale(tick)}
              x2={width - plotRight}
              y2={yScale(tick)}
              stroke="#e3e3e3"
              strokeWidth="1"
            />
            <text
              x={left - 8}
              y={yScale(tick) + 4}
              textAnchor="end"
              fontSize="11"
              fill="#4a4a4a"
            >
              {tick}
            </text>
          </g>
        ))}

        {xTicks.map((tick) => (
          <g key={`x-${tick}`}>
            <line
              x1={xScale(tick)}
              y1={top}
              x2={xScale(tick)}
              y2={height - bottom}
              stroke="#ececec"
              strokeWidth="1"
            />
            <text
              x={xScale(tick)}
              y={height - bottom + 22}
              textAnchor="middle"
              fontSize="11"
              fill="#4a4a4a"
            >
              {tick}
            </text>
          </g>
        ))}

        <line
          x1={left}
          y1={top}
          x2={left}
          y2={height - bottom}
          stroke="#666"
          strokeWidth="1"
        />
        <line
          x1={left}
          y1={height - bottom}
          x2={width - plotRight}
          y2={height - bottom}
          stroke="#666"
          strokeWidth="1"
        />

        <text
          x={width / 2}
          y={height - 14}
          textAnchor="middle"
          fontSize="12"
          fontStyle="italic"
          fill="#222"
        >
          Game Time (mins)
        </text>
        <text
          x="18"
          y={height / 2}
          textAnchor="middle"
          fontSize="12"
          fontStyle="italic"
          fill="#222"
          transform={`rotate(-90 18 ${height / 2})`}
        >
          Score (pts)
        </text>

        {chartModel.lineSeries.map((points, index) => (
          <path
            key={`line-${index}`}
            d={linePath(points)}
            fill="none"
            stroke={seriesStyles[index].color}
            strokeWidth="3"
            strokeDasharray={seriesStyles[index].dash}
          />
        ))}

        {hasData &&
          chartModel.pointSeries.map((points, index) =>
            points.map((point) => (
              <circle
                key={`point-${point.key}`}
                cx={xScale(point.x)}
                cy={yScale(point.y)}
                r="4"
                fill={seriesStyles[index].color}
                fillOpacity={index % 2 === 1 ? 0.55 : 1}
                stroke={seriesStyles[index].color}
                strokeWidth="1"
                style={{ cursor: point.tooltip ? "pointer" : "default" }}
                onMouseEnter={() =>
                  setHoverState(
                    point.tooltip
                      ? {
                          text: point.tooltip,
                          xPct: (xScale(point.x) / width) * 100,
                          yPct: (yScale(point.y) / height) * 100,
                          anchorRight: xScale(point.x) > width * 0.58,
                        }
                      : null,
                  )
                }
                onMouseLeave={() => setHoverState(null)}
              />
            )),
          )}
      </svg>
    </div>
  );
}

// ─── Helpers for exp-score clustering ────────────────────────────────────────
function clusterByAttack(shots) {
  const clusters = [];
  for (let shot of shots) {
    if (shot.newAttack === "Yes") {
      clusters.push([shot]);
    } else {
      if (clusters.length === 0) {
        clusters.push([shot]);
      } else {
        clusters[clusters.length - 1].push(shot);
      }
    }
  }
  return clusters;
}

function sumMaxExpectedScore(shots) {
  return clusterByAttack(shots)
    .map((cluster) =>
      cluster.reduce((mx, s) => Math.max(mx, s.expectedScore), 0),
    )
    .reduce((sum, mx) => sum + mx, 0);
}

// ─── Sub-component: one row in the Games table ─────────────────────────────
function GameScoreRow({ game, isSelected, onSelect }) {
  const gameType = game?.GameType || "Mens";
  let { data: rowShots = [] } = useGetShotsByGameIdQuery({ gameId: game._id });
  rowShots = rowShots?.shotData || [];

  const outcomeValue = useCallback(
    (v) => (v === "Goal" ? 3 : v === "2 Point" ? 2 : v === "Point" ? 1 : 0),
    [],
  );

  const summarize = (team) => {
    const shots = rowShots.filter((s) => s.teamShot === team);
    const exp = [1, 2, 3]
      .reduce(
        (sum, half) => sum + sumMaxExpectedScore(shots.filter((s) => s.gameHalf === half)),
        0,
      )
      .toFixed(2);
    const goals = shots.filter((s) => s.shotOutcome === "Goal").length;
    const two2 = shots.filter((s) => s.shotOutcome === "2 Point").length;
    const ones = shots.filter((s) => s.shotOutcome === "Point").length;
    const pts = shots.reduce((sum, s) => sum + outcomeValue(s.shotOutcome), 0);
    return { exp, goals, two2, ones, pts };
  };

  const home = summarize(game.HomeTeam);
  const away = summarize(game.AwayTeam);

  const scoreSummary =
    `[${home.exp}] ${home.goals}-${home.two2}-${home.ones} (${home.pts})` +
    ` : (${away.pts}) ${away.goals}-${away.two2}-${away.ones} [${away.exp}]`;

  return (
    <tr
      onClick={() => onSelect(game)}
      className={isSelected ? "table-active" : ""}
      style={{ cursor: "pointer" }}
    >
      <td>{new Date(game.GameDate).toLocaleDateString()}</td>
      <td>{game.Competition}</td>
      <td>{gameType}</td>
      <td>{game.HomeTeam}</td>
      <td>{scoreSummary}</td>
      <td>{game.AwayTeam}</td>
    </tr>
  );
}

// ─── Main screen component ───────────────────────────────────────────────────
const ShotAnalysisScreen = () => {
  const HALF_DURATION_MINUTES = 35;
  const TEAM_COLOR_HEX = {
    red: "#d32f2f",
    blue: "#1976d2",
    brown: "#6d4c41",
    cyan: "#00838f",
    green: "#2e7d32",
    orange: "#ef6c00",
    purple: "#6a1b9a",
  };
  const renderPerfLegend = (homeTeam, awayTeam, homeColor, awayColor) =>
    (() => {
      const truncate = (value, max = 12) => {
        const text = String(value || "");
        return text.length > max ? `${text.slice(0, max - 3)}...` : text;
      };
      const homeShort = truncate(homeTeam);
      const awayShort = truncate(awayTeam);
      return (
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: 4,
            padding: "0",
            background: "transparent",
            fontSize: "12px",
            lineHeight: 1.1,
            color: "#212529",
          }}
        >
          <div className="d-flex align-items-center gap-1">
            <span style={{ width: 12, borderTop: `2px solid ${homeColor}` }} />
            <small title={homeTeam}>{homeShort}</small>
          </div>
          <div className="d-flex align-items-center gap-1">
            <span style={{ width: 12, borderTop: `2px dashed ${homeColor}` }} />
            <small title={`${homeTeam} xP`}>{homeShort} xP</small>
          </div>
          <div className="d-flex align-items-center gap-1">
            <span style={{ width: 12, borderTop: `2px solid ${awayColor}` }} />
            <small title={awayTeam}>{awayShort}</small>
          </div>
          <div className="d-flex align-items-center gap-1">
            <span style={{ width: 12, borderTop: `2px dashed ${awayColor}` }} />
            <small title={`${awayTeam} xP`}>{awayShort} xP</small>
          </div>
        </div>
      );
    })();
  // — Games table state & hooks —
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [search, setSearch] = useState("");
  const sortKey = "GameDate";
  const [sortOrder, setSortOrder] = useState("desc");

  const {
    data: gamesData = { entries: [], total: 0, pages: 1 },
    isLoading: gamesLoading,
    error: gamesError,
  } = useGetGameDataQuery(
    { page, limit, search, sort: sortKey, order: sortOrder },
    { refetchOnMountOrArgChange: true },
  );

  // — Selected game & its shots —
  const [selectedGame, setSelectedGame] = useState(null);
  const homeSeriesColor =
    TEAM_COLOR_HEX[selectedGame?.HomeTeamColor] || TEAM_COLOR_HEX.green;
  const awaySeriesColor =
    TEAM_COLOR_HEX[selectedGame?.AwayTeamColor] || TEAM_COLOR_HEX.blue;
  let {
    data: shotsData = [],
    isLoading: shotsLoading,
    error: shotsError,
  } = useGetShotsByGameIdQuery(
    { gameId: selectedGame?._id },
    { skip: !selectedGame },
  );
  shotsData = shotsData?.shotData || [];

  const normalizedShotsData = useMemo(() => {
    return shotsData.map((shot) => ({
      ...shot,
      gameTimeSeconds: Number(shot?.gameTimeSeconds) || 0,
    }))
  }, [shotsData])

  // Precompute which shot IDs count toward XP (one per attack cluster)
  const xpShotIds = useMemo(() => {
    const clusters = clusterByAttack(normalizedShotsData)
    return new Set(
      clusters.map(cluster =>
        cluster.reduce((best, s) => s.expectedScore > best.expectedScore ? s : best, cluster[0])._id
      )
    )
  }, [normalizedShotsData])

  
  // Whatever this function below is checking for messed up the result in the shot map xP value.
  // The result of this useMemo was originally passed as a prop into the ShotMapWithFilters component
  // Whatever "zero out expect... " means seemed to break what the expected value for xP is.
  //
  // Refactor used to fix:  Pass "normalizedShotsData" directly into the ShotMapWithFilters instead of
  // passing through this memo, uncomment at your own peril.
  /*


  // Prepare data for the map: zero out expectedScore for non-XP shots
  const mapShotsData = useMemo(() =>
    normalizedShotsData.map(s => ({
      ...s,
      //expectedScore: xpShotIds.has(s._id) ? s.expectedScore : 0
    }))
  , [normalizedShotsData, xpShotIds])

  */

  // — Collapsible panel toggles —
  const [openPerf, setOpenPerf] = useState(true);
  const [openTeam, setOpenTeam] = useState(true);
  const [openPlayer, setOpenPlayer] = useState(true);
  const [openMap, setOpenMap] = useState(true);
  const [half1HoverState, setHalf1HoverState] = useState(null);
  const [half2HoverState, setHalf2HoverState] = useState(null);
  const pageTitleRef = useRef(null);
  const gamesSectionRef = useRef(null);
  const noGamesAlertRef = useRef(null);
  const noSelectionAlertRef = useRef(null);
  const performanceCardRef = useRef(null);
  const teamCardRef = useRef(null);
  const playerCardRef = useRef(null);
  const mapCardRef = useRef(null);
  const [showTutorial, setShowTutorial] = useState(false);
  const [tutorialStepIndex, setTutorialStepIndex] = useState(0);
  const [tutorialRect, setTutorialRect] = useState(null);
  const [tutorialAutoSelectedGame, setTutorialAutoSelectedGame] =
    useState(false);
  const performanceExportRef = useRef(null);

  // outcome → points helper
  const outcomeValue = useCallback(
    (v) => (v === "Goal" ? 3 : v === "2 Point" ? 2 : v === "Point" ? 1 : 0),
    [],
  );

  // — GAME PERFORMANCE DATA —
  const performanceData = useMemo(() => {
    if (!selectedGame) return { half1: [], half2: [] };
    const home = selectedGame.HomeTeam;
    const away = selectedGame.AwayTeam;
    const byHalf = {
      1: normalizedShotsData.filter((s) => s.gameHalf === 1),
      2: normalizedShotsData.filter((s) => s.gameHalf === 2),
    };

    const build = (arr) => {
      const clusters = clusterByAttack(arr);
      const xpShots = clusters.map((cluster) =>
        cluster.reduce(
          (m, s) => (s.expectedScore > m.expectedScore ? s : m),
          cluster[0],
        ),
      );
      const xpIds = new Set(xpShots.map((s) => s._id));

      let cumHomePts = 0,
        cumAwayPts = 0,
        cumHomeXp = 0,
        cumAwayXp = 0;
      let lastHomePts = 0,
        lastAwayPts = 0,
        lastHomeXp = 0,
        lastAwayXp = 0;
      const rows = [
        [
          "Time",
          home,
          { type: "string", role: "tooltip" },
          `${home} xP`,
          { type: "string", role: "tooltip" },
          away,
          { type: "string", role: "tooltip" },
          `${away} xP`,
          { type: "string", role: "tooltip" },
          `${home} point`,
          { type: "string", role: "tooltip" },
          `${home} xP point`,
          { type: "string", role: "tooltip" },
          `${away} point`,
          { type: "string", role: "tooltip" },
          `${away} xP point`,
          { type: "string", role: "tooltip" },
        ],
      ];

      if (!arr.length) {
        rows.push([
          0,
          0,
          "",
          { v: 0, f: "0.0" },
          "",
          0,
          "",
          { v: 0, f: "0.0" },
          "",
          null,
          "",
          null,
          "",
          null,
          "",
          null,
          "",
        ]);
        rows.push([
          HALF_DURATION_MINUTES,
          0,
          "",
          { v: 0, f: "0.0" },
          "",
          0,
          "",
          { v: 0, f: "0.0" },
          "",
          null,
          "",
          null,
          "",
          null,
          "",
          null,
          "",
        ]);
        return rows;
      }

      arr
        .sort((a, b) => a.gameTimeSeconds - b.gameTimeSeconds)
        .forEach((s) => {
          const displayTimeSeconds = toHalfRelativeSeconds(
            s.gameHalf,
            s.gameTimeSeconds,
          );
          const t = displayTimeSeconds / 60;
          const timeLabel = formatTooltipTime(displayTimeSeconds);
          const pts = outcomeValue(s.shotOutcome);
          if (s.teamShot === home) cumHomePts += pts;
          else cumAwayPts += pts;

          const xp = xpIds.has(s._id) ? s.expectedScore : 0;
          if (s.teamShot === home) cumHomeXp += xp;
          else cumAwayXp += xp;

          const solidTooltip = [
            `Time: ${timeLabel}`,
            `Shot Type: ${s.shotType || "Unknown"}`,
            `Shot Outcome: ${s.shotOutcome || "Unknown"}`,
            `Expected Score: ${Number(s.expectedScore || 0).toFixed(2)}`,
          ].join("\n");
          const dashedTooltip = [
            `Time: ${timeLabel}`,
            `Player: ${s.playerName || "Unknown"}`,
            `Expected Score: ${Number(s.expectedScore || 0).toFixed(2)}`,
          ].join("\n");

          rows.push([
            t,
            lastHomePts,
            "",
            lastHomeXp,
            "",
            lastAwayPts,
            "",
            lastAwayXp,
            "",
            null,
            "",
            null,
            "",
            null,
            "",
            null,
            "",
          ]);

          rows.push([
            t,
            cumHomePts,
            "",
            { v: cumHomeXp, f: cumHomeXp.toFixed(1) },
            "",
            cumAwayPts,
            "",
            { v: cumAwayXp, f: cumAwayXp.toFixed(1) },
            "",
            s.teamShot === home ? cumHomePts : null,
            s.teamShot === home ? solidTooltip : "",
            s.teamShot === home && xpIds.has(s._id)
              ? { v: cumHomeXp, f: cumHomeXp.toFixed(1) }
              : null,
            s.teamShot === home && xpIds.has(s._id) ? dashedTooltip : "",
            s.teamShot === away ? cumAwayPts : null,
            s.teamShot === away ? solidTooltip : "",
            s.teamShot === away && xpIds.has(s._id)
              ? { v: cumAwayXp, f: cumAwayXp.toFixed(1) }
              : null,
            s.teamShot === away && xpIds.has(s._id) ? dashedTooltip : "",
          ]);

          lastHomePts = cumHomePts;
          lastAwayPts = cumAwayPts;
          lastHomeXp = cumHomeXp;
          lastAwayXp = cumAwayXp;
        });

      return rows;
    };

    return {
      half1: build(byHalf[1]),
      half2: build(byHalf[2]),
    };
  }, [normalizedShotsData, selectedGame, outcomeValue]);

  // — SUMMARY CALCULATION FOR OVERVIEW —
  const summary = useMemo(() => {
    const getStats = (rows) => {
      if (rows.length <= 1)
        return { homePts: 0, homeXp: 0, awayPts: 0, awayXp: 0 };
      const last = rows[rows.length - 1];
      const homeXpCell = last[3];
      const awayXpCell = last[7];
      return {
        homePts: Number(last[1] || 0),
        homeXp:
          typeof homeXpCell === "object" && homeXpCell !== null
            ? Number(homeXpCell.v || 0)
            : Number(homeXpCell || 0),
        awayPts: Number(last[5] || 0),
        awayXp:
          typeof awayXpCell === "object" && awayXpCell !== null
            ? Number(awayXpCell.v || 0)
            : Number(awayXpCell || 0),
      };
    };
    const h1 = getStats(performanceData.half1);
    const h2 = getStats(performanceData.half2);
    return {
      first: h1,
      second: h2,
      total: {
        homePts: h1.homePts + h2.homePts,
        homeXp: h1.homeXp + h2.homeXp,
        awayPts: h1.awayPts + h2.awayPts,
        awayXp: h1.awayXp + h2.awayXp,
      },
    };
  }, [performanceData]);

  const hasHalf1Data = useMemo(
    () => normalizedShotsData.some((s) => s.gameHalf === 1),
    [normalizedShotsData],
  );
  const hasHalf2Data = useMemo(
    () => normalizedShotsData.some((s) => s.gameHalf === 2),
    [normalizedShotsData],
  );

  // — Team & Player Efficiency —
  const makeEfficiency = useCallback(
    (fn) => {
      const arr = normalizedShotsData.filter(fn);
      const num = arr.length;
      const scored = arr.filter((s) => outcomeValue(s.shotOutcome) > 0).length;
      const points = arr.reduce((c, s) => c + outcomeValue(s.shotOutcome), 0);
      const xPoints = arr.reduce((c, s) => c + s.expectedScore, 0);
      return {
        num,
        scored,
        conversionRate: num > 0 ? `${Math.round((scored / num) * 100)}%` : "",
        points,
        xPoints: xPoints.toFixed(1),
        eff: xPoints > 0 ? (points / xPoints).toFixed(2) : "—",
      };
    },
    [normalizedShotsData, outcomeValue],
  );

  const teamTables = useMemo(() => {
    if (!selectedGame) return { home: [], away: [] };
    const h = selectedGame.HomeTeam,
      a = selectedGame.AwayTeam;
    return {
      home: [
        {
          type: "Point",
          ...makeEfficiency(
            (s) => s.teamShot === h && s.shotType === "Point attempt",
          ),
        },
        {
          type: "2 Point",
          ...makeEfficiency(
            (s) => s.teamShot === h && s.shotType === "2 Point attempt",
          ),
        },
        {
          type: "Goal",
          ...makeEfficiency(
            (s) => s.teamShot === h && s.shotType === "Goal attempt",
          ),
        },
        {
          type: "1st Half",
          ...makeEfficiency((s) => s.teamShot === h && s.gameHalf === 1),
        },
        {
          type: "2nd Half",
          ...makeEfficiency((s) => s.teamShot === h && s.gameHalf === 2),
        },
        { type: "All", ...makeEfficiency((s) => s.teamShot === h) },
      ],
      away: [
        {
          type: "Point",
          ...makeEfficiency(
            (s) => s.teamShot === a && s.shotType === "Point attempt",
          ),
        },
        {
          type: "2 Point",
          ...makeEfficiency(
            (s) => s.teamShot === a && s.shotType === "2 Point attempt",
          ),
        },
        {
          type: "Goal",
          ...makeEfficiency(
            (s) => s.teamShot === a && s.shotType === "Goal attempt",
          ),
        },
        {
          type: "1st Half",
          ...makeEfficiency((s) => s.teamShot === a && s.gameHalf === 1),
        },
        {
          type: "2nd Half",
          ...makeEfficiency((s) => s.teamShot === a && s.gameHalf === 2),
        },
        { type: "All", ...makeEfficiency((s) => s.teamShot === a) },
      ],
    };
  }, [selectedGame, makeEfficiency]);

  const playerTables = useMemo(() => {
    if (!selectedGame) return { home: [], away: [] };
    const h = selectedGame.HomeTeam,
      a = selectedGame.AwayTeam;
    const build = (team) => {
      const names = [
        ...new Set(
          normalizedShotsData
            .filter((s) => s.teamShot === team)
            .map((s) => s.playerName),
        ),
      ];
      return names.flatMap((n, i) => [
        {
          key: `${n}-P-${i}`,
          player: n,
          category: "Point",
          ...makeEfficiency(
            (s) =>
              s.teamShot === team &&
              s.playerName === n &&
              s.shotType === "Point attempt",
          ),
        },
        {
          key: `${n}-2P-${i}`,
          player: n,
          category: "2 Point",
          ...makeEfficiency(
            (s) =>
              s.teamShot === team &&
              s.playerName === n &&
              s.shotType === "2 Point attempt",
          ),
        },
        {
          key: `${n}-G-${i}`,
          player: n,
          category: "Goal",
          ...makeEfficiency(
            (s) =>
              s.teamShot === team &&
              s.playerName === n &&
              s.shotType === "Goal attempt",
          ),
        },
      ]);
    };
    return { home: build(h), away: build(a) };
  }, [normalizedShotsData, selectedGame, makeEfficiency]);

  // — Sorting helpers —
  const [playerSortCol, setPlayerSortCol] = useState("player");
  const [playerSortAsc, setPlayerSortAsc] = useState(true);
  const handlePlayerSort = (col) => {
    if (playerSortCol === col) setPlayerSortAsc(!playerSortAsc);
    else {
      setPlayerSortCol(col);
      setPlayerSortAsc(true);
    }
  };
  const sortedPlayerRows = (rows) =>
    [...rows]
      .filter((r) => r.eff !== "—")
      .sort((a, b) => {
        const aVal = a[playerSortCol],
          bVal = b[playerSortCol];
        if (["player", "category"].includes(playerSortCol)) {
          return playerSortAsc
            ? String(aVal).localeCompare(bVal)
            : String(bVal).localeCompare(aVal);
        }
        return playerSortAsc ? aVal - bVal : bVal - aVal;
      });

  //select by year
  const startYear = 2020;
  const currYear = new Date().getFullYear();
  const yearsArray = Array.from(
    { length: currYear - startYear + 1 },
    (_, i) => currYear - i,
  );
  const [selectedYear, setSelectedYear] = useState("All");

  //filter by year first
  const filteredGames = gamesData.entries.filter(
    (g) => selectedYear === "All" || g.GameDate.split("-")[0] === selectedYear,
  );
  const hasTutorialGameAvailable =
    Boolean(selectedGame) || filteredGames.length > 0;

  const tutorialSteps = useMemo(() => {
    const baseSteps = [
      {
        title: "Game Analysis Overview",
        body: "This screen is for match-by-match analysis in Gaelic football. Start by choosing one game, then use the panels below to review scoring flow, efficiency, and shot locations.",
        ref: pageTitleRef,
      },
      {
        title: "Game Selection",
        body: "Use the search, year filter, and match list to choose the game you want to review. Every analysis panel below updates from the selected match.",
        ref: gamesSectionRef,
      },
    ];

    if (!hasTutorialGameAvailable) {
      return [
        ...baseSteps,
        {
          title: "No Games Available",
          body: "There are no games available to analyse yet. For a new user, this is expected until game data has been added or imported.",
          ref: noGamesAlertRef,
        },
        {
          title: "What Unlocks Next",
          body: "Once you add a game, this screen unlocks the full match breakdown: scoring flow, team efficiency, player efficiency, and the shot map. This message is the handoff point that tells you to select a game first.",
          ref: noSelectionAlertRef,
        },
        {
          title: "Next Step",
          body: "To start using Game Analysis, go to Game Data, add a match, then return here and select it from the list. After that, the full analysis cards will appear below.",
          ref: gamesSectionRef,
        },
      ];
    }

    return [
      ...baseSteps,
      {
        title: "Game Performance",
        body: "This panel tracks scoring flow by half. It helps you see when each team scored, where momentum shifted, and how actual scoring compared with expected score.",
        ref: performanceCardRef,
      },
      {
        title: "Team Shot Efficiency",
        body: "This compares the two teams by shot type. It is useful for seeing which side created better chances and which side converted more efficiently.",
        ref: teamCardRef,
      },
      {
        title: "Player Shot Efficiency",
        body: "This breaks performance down by player, so you can see who took the most shots, who scored efficiently, and who may have forced lower-value attempts.",
        ref: playerCardRef,
      },
      {
        title: "Shot Map",
        body: "The shot map shows where attempts came from on the pitch. In Gaelic football, this is useful for spotting central shooting, wide shooting, and team shot-selection patterns by zone.",
        ref: mapCardRef,
      },
    ];
  }, [hasTutorialGameAvailable]);

  useEffect(() => {
    if (!showTutorial) return;
    if (tutorialStepIndex > tutorialSteps.length - 1) {
      setTutorialStepIndex(Math.max(0, tutorialSteps.length - 1));
    }
  }, [showTutorial, tutorialStepIndex, tutorialSteps]);

  useEffect(() => {
    if (!showTutorial) return;
    const needsSelectedGame = tutorialStepIndex >= 2;
    if (needsSelectedGame && !selectedGame && filteredGames.length > 0) {
      setSelectedGame(filteredGames[0]);
      setTutorialAutoSelectedGame(true);
    }
    if (tutorialStepIndex >= 2) setOpenPerf(true);
    if (tutorialStepIndex >= 3) setOpenTeam(true);
    if (tutorialStepIndex >= 4) setOpenPlayer(true);
    if (tutorialStepIndex >= 5) setOpenMap(true);
  }, [showTutorial, tutorialStepIndex, selectedGame, filteredGames]);

  const updateTutorialRect = useCallback(() => {
    if (!showTutorial) {
      setTutorialRect(null);
      return;
    }
    const step = tutorialSteps[tutorialStepIndex];
    const node = step?.ref?.current;
    if (!node) {
      setTutorialRect(null);
      return;
    }
    const rect = node.getBoundingClientRect();
    setTutorialRect({
      top: Math.max(8, rect.top - 8),
      left: Math.max(8, rect.left - 8),
      width: rect.width + 16,
      height: rect.height + 16,
    });
  }, [showTutorial, tutorialStepIndex, tutorialSteps]);

  useEffect(() => {
    if (!showTutorial) return undefined;
    const step = tutorialSteps[tutorialStepIndex];
    step?.ref?.current?.scrollIntoView({
      behavior: "smooth",
      block: tutorialStepIndex >= 2 ? "start" : "center",
    });
    const refresh = () => updateTutorialRect();
    refresh();
    const timeoutId = window.setTimeout(refresh, 220);
    const secondTimeoutId = window.setTimeout(refresh, 420);
    const intervalId = window.setInterval(refresh, 70);
    const stopIntervalId = window.setTimeout(() => {
      window.clearInterval(intervalId);
    }, 800);
    window.addEventListener("resize", refresh);
    window.addEventListener("scroll", refresh, true);
    return () => {
      window.clearTimeout(timeoutId);
      window.clearTimeout(secondTimeoutId);
      window.clearTimeout(stopIntervalId);
      window.clearInterval(intervalId);
      window.removeEventListener("resize", refresh);
      window.removeEventListener("scroll", refresh, true);
    };
  }, [showTutorial, tutorialStepIndex, tutorialSteps, updateTutorialRect]);

  const startTutorial = () => {
    setTutorialStepIndex(0);
    setTutorialAutoSelectedGame(false);
    setShowTutorial(true);
  };

  const closeTutorial = () => {
    if (tutorialAutoSelectedGame) {
      setSelectedGame(null);
      setTutorialAutoSelectedGame(false);
    }
    setShowTutorial(false);
    setTutorialRect(null);
  };

  const nextTutorialStep = () => {
    if (tutorialStepIndex >= tutorialSteps.length - 1) {
      closeTutorial();
      return;
    }
    setTutorialStepIndex((prev) => prev + 1);
  };

  const prevTutorialStep = () => {
    setTutorialStepIndex((prev) => Math.max(prev - 1, 0));
  };

  const getTutorialCardStyle = () => {
    if (!tutorialRect) return {};
    const cardWidth = Math.min(360, window.innerWidth - 32);
    const gap = 16;
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;
    const placeBelow =
      tutorialRect.top + tutorialRect.height + 220 + gap < viewportHeight;
    const top = placeBelow
      ? tutorialRect.top + tutorialRect.height + gap
      : Math.max(16, tutorialRect.top - 220 - gap);
    const preferredLeft = tutorialRect.left;
    const maxLeft = Math.max(16, viewportWidth - cardWidth - 16);
    const left = Math.min(Math.max(16, preferredLeft), maxLeft);
    return {
      position: "fixed",
      top,
      left,
      width: cardWidth,
      zIndex: 1062,
      boxShadow: "0 16px 40px rgba(0, 0, 0, 0.35)",
      transition: "top 160ms ease, left 160ms ease",
    };
  };

  const handleDownloadPerformanceCharts = async () => {
    if (!performanceExportRef.current || !selectedGame) return;
    const canvas = await html2canvas(performanceExportRef.current, {
      backgroundColor: "#212529",
      scale: 2,
    });
    const uri = canvas.toDataURL("image/png");
    const safeHome = String(selectedGame.HomeTeam || "home").replace(
      /[^\w-]+/g,
      "_",
    );
    const safeAway = String(selectedGame.AwayTeam || "away").replace(
      /[^\w-]+/g,
      "_",
    );
    const link = document.createElement("a");
    link.href = uri;
    link.download = `${safeHome}-vs-${safeAway}-performance.png`;
    link.click();
  };

  const TEAM_EFFICIENCY_ORDER = [
    { type: "Goal", key: "Goal" },
    { type: "2 Point", key: "2 Point" },
    { type: "Point", key: "Point" },
    { type: "1st Half", key: "1st Half" },
    { type: "2nd Half", key: "2nd Half" },
    { type: "All", key: "All" },
  ];

  const getStaticTeamRows = (rows = []) => {
    const map = {};
    rows.forEach((r) => {
      map[r.type] = r;
    });

    return TEAM_EFFICIENCY_ORDER.map((def) => {
      const r = map[def.type];

      // If row doesn't exist OR has zero attempts → treat as no data
      if (!r || r.num === 0) {
        return {
          type: def.type,
          num: "-",
          scored: "-",
          conversionRate: "",
          points: "-",
          xPoints: "-",
          eff: "-",
        };
      }

      return {
        type: def.type,
        num: r.num,
        scored: r.scored,
        conversionRate: r.conversionRate,
        points: r.points,
        xPoints: r.xPoints,
        eff: r.eff,
      };
    });
  };

  return (
    <Container fluid className="py-4 app-section-page">
      <title>Game Analysis | ShotArc</title>
      <ScreenTabs items={ANALYSIS_TABS} />
      <div
        className="d-flex align-items-center justify-content-between gap-3 mb-3"
        ref={pageTitleRef}
      >
        <h2 className="app-page-title mb-0">Game Analysis</h2>
        <Button
          variant="outline-light"
          size="sm"
          className="d-inline-flex align-items-center gap-2"
          onClick={startTutorial}
        >
          <FaQuestionCircle />
          Help Tour
        </Button>
      </div>

      <div
        className="bg-dark border border-warning rounded p-3 mb-4"
        ref={gamesSectionRef}
      >
        {/* — Games table — */}
        <Row className="mt-0 mb-3 align-items-end">
          <Col md={3}>
            <Form.Label>Search</Form.Label>
            <Form.Control
              placeholder="Team or Competition..."
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setPage(1);
              }}
            />
          </Col>
          <Col md={2}>
            <Form.Label>Per page</Form.Label>
            <Form.Select
              value={limit}
              onChange={(e) => {
                setLimit(+e.target.value);
                setPage(1);
              }}
            >
              {[10, 25, 50, 100].map((n) => (
                <option key={n}>{n}</option>
              ))}
            </Form.Select>
          </Col>
          <Col md={2}>
            <Form.Label>Year</Form.Label>
            <Form.Select
              value={selectedYear}
              onChange={(e) => {
                setSelectedYear(e.target.value);
                setSelectedGame(null);
              }}
            >
              <option value="All">All</option>
              {yearsArray.map((year) => (
                <option value={`${year}`}>{year}</option>
              ))}
            </Form.Select>
          </Col>
          <Col md={5} className="text-end">
            {gamesData.total > 0 && (
              <span className="text-light">
                Showing {(page - 1) * limit + 1}–
                {Math.min(page * limit, gamesData.total)} of {gamesData.total}
              </span>
            )}
          </Col>
        </Row>

        {gamesLoading ? (
          <Loader />
        ) : gamesError ? (
          <Message variant="danger">{gamesError.error}</Message>
        ) : filteredGames.length === 0 ? (
          <Alert className="app-empty-state mb-0" ref={noGamesAlertRef}>
            No games found
          </Alert>
        ) : (
          <Table striped bordered hover responsive className="app-table">
            <thead className="table-dark">
              <tr>
                <th
                  onClick={() =>
                    setSortOrder((o) => (o === "asc" ? "desc" : "asc"))
                  }
                >
                  Date{" "}
                  {sortKey === "GameDate" ? (
                    sortOrder === "asc" ? (
                      <FaSortUp />
                    ) : (
                      <FaSortDown />
                    )
                  ) : null}
                </th>
                <th>Competition</th>
                <th>Type</th>
                <th>Home</th>
                <th>Score [xP]</th>
                <th>Away</th>
              </tr>
            </thead>
            <tbody>
              {filteredGames.map((g) => (
                <GameScoreRow
                  key={g._id}
                  game={g}
                  isSelected={selectedGame?._id === g._id}
                  onSelect={setSelectedGame}
                />
              ))}
            </tbody>
          </Table>
        )}

        {gamesData.pages > 1 && (
          <Pagination className="justify-content-center app-pagination">
            <Pagination.Prev
              disabled={page === 1}
              onClick={() => setPage((p) => p - 1)}
            />
            {[...Array(gamesData.pages)].map((_, i) => (
              <Pagination.Item
                key={i}
                active={i + 1 === page}
                onClick={() => setPage(i + 1)}
              >
                {i + 1}
              </Pagination.Item>
            ))}
            <Pagination.Next
              disabled={page === gamesData.pages}
              onClick={() => setPage((p) => p + 1)}
            />
          </Pagination>
        )}
      </div>

      {/* — Analysis panels — */}
      {!selectedGame ? (
        <Alert className="app-empty-state mb-4" ref={noSelectionAlertRef}>
          Please select a game above to view details.
        </Alert>
      ) : shotsLoading ? (
        <Loader />
      ) : shotsError ? (
        <Message variant="danger">Failed to load shots</Message>
      ) : (
        <>
          {/* Game Performance */}
          <Card className="border-warning mb-4" ref={performanceCardRef}>
            <Card.Header
              className="bg-warning text-dark d-flex justify-content-between align-items-center"
              style={{ cursor: "pointer" }}
              onClick={() => setOpenPerf((o) => !o)}
            >
              <strong>Game Performance</strong>
              {openPerf ? <FaChevronUp /> : <FaChevronDown />}
            </Card.Header>
            {openPerf && (
              <Card.Body className="bg-dark text-white">
                <Row className="text-center mb-3">
                  <Col>
                    <div>
                      <strong>1st Half:</strong>
                    </div>
                    <div>
                      <span style={{ color: homeSeriesColor }}>
                        [{summary.first.homeXp.toFixed(2)}]
                      </span>
                      &nbsp;{summary.first.homePts}
                      &nbsp;v&nbsp;
                      {summary.first.awayPts}
                      &nbsp;
                      <span style={{ color: awaySeriesColor }}>
                        [{summary.first.awayXp.toFixed(2)}]
                      </span>
                    </div>
                  </Col>
                  <Col>
                    <div style={{ color: awaySeriesColor, fontWeight: "bold" }}>
                      {selectedGame.HomeTeam} v {selectedGame.AwayTeam}
                    </div>
                    <div style={{ fontSize: "1.25rem" }}>
                      <span style={{ color: homeSeriesColor }}>
                        [{summary.total.homeXp.toFixed(2)}]
                      </span>
                      &nbsp;{summary.total.homePts}
                      &nbsp;v&nbsp;
                      {summary.total.awayPts}
                      &nbsp;
                      <span style={{ color: awaySeriesColor }}>
                        [{summary.total.awayXp.toFixed(2)}]
                      </span>
                    </div>
                  </Col>
                  <Col>
                    <div>
                      <strong>2nd Half:</strong>
                    </div>
                    <div>
                      <span style={{ color: homeSeriesColor }}>
                        [{summary.second.homeXp.toFixed(2)}]
                      </span>
                      &nbsp;{summary.second.homePts}
                      &nbsp;v&nbsp;
                      {summary.second.awayPts}
                      &nbsp;
                      <span style={{ color: awaySeriesColor }}>
                        [{summary.second.awayXp.toFixed(2)}]
                      </span>
                    </div>
                  </Col>
                </Row>
                <Row className="g-3" ref={performanceExportRef}>
                  <Col lg={6} className="mb-3 mb-lg-0">
                    {!hasHalf1Data && (
                      <Alert className="app-empty-state mb-2 text-center">
                        No 1st-half shots found.
                      </Alert>
                    )}
                    <div style={{ position: "relative" }}>
                      <PerformanceChart
                        title={`${selectedGame.HomeTeam} vs ${selectedGame.AwayTeam} - 1st Half`}
                        rows={performanceData.half1}
                        hasData={hasHalf1Data}
                        homeColor={homeSeriesColor}
                        awayColor={awaySeriesColor}
                        hoverState={half1HoverState}
                        setHoverState={setHalf1HoverState}
                        halfDurationMinutes={HALF_DURATION_MINUTES}
                        legend={renderPerfLegend(
                          selectedGame.HomeTeam,
                          selectedGame.AwayTeam,
                          homeSeriesColor,
                          awaySeriesColor,
                        )}
                      />
                    </div>
                  </Col>

                  <Col lg={6}>
                    {!hasHalf2Data && (
                      <Alert className="app-empty-state mb-2 text-center">
                        No 2nd-half shots found.
                      </Alert>
                    )}
                    <div style={{ position: "relative" }}>
                      <PerformanceChart
                        title={`${selectedGame.HomeTeam} vs ${selectedGame.AwayTeam} - 2nd Half`}
                        rows={performanceData.half2}
                        hasData={hasHalf2Data}
                        homeColor={homeSeriesColor}
                        awayColor={awaySeriesColor}
                        hoverState={half2HoverState}
                        setHoverState={setHalf2HoverState}
                        halfDurationMinutes={HALF_DURATION_MINUTES}
                        legend={renderPerfLegend(
                          selectedGame.HomeTeam,
                          selectedGame.AwayTeam,
                          homeSeriesColor,
                          awaySeriesColor,
                        )}
                      />
                    </div>
                  </Col>
                </Row>
                <div className="d-flex justify-content-end mt-2">
                  <Button
                    size="sm"
                    variant="outline-light"
                    onClick={handleDownloadPerformanceCharts}
                  >
                    <FaDownload className="me-1" />
                    Download Performance Charts
                  </Button>
                </div>
              </Card.Body>
            )}
          </Card>

          {/* Team Shot Efficiency */}
          <Card className="border-warning mb-4" ref={teamCardRef}>
            <Card.Header
              className="bg-warning text-dark d-flex justify-content-between align-items-center"
              style={{ cursor: "pointer" }}
              onClick={() => setOpenTeam((o) => !o)}
            >
              <strong>Team Shot Efficiency</strong>
              {openTeam ? <FaChevronUp /> : <FaChevronDown />}
            </Card.Header>
            {openTeam && (
              <Card.Body className="bg-dark text-white">
                <Row>
                  {["home", "away"].map((side, i) => {
                    const rows = getStaticTeamRows(teamTables[side]);
                    return (
                      <Col key={i}>
                        <h5 className="text-center">
                          {side === "home"
                            ? selectedGame.HomeTeam
                            : selectedGame.AwayTeam}
                        </h5>
                        <Table
                          striped
                          bordered
                          hover
                          size="sm"
                          className="app-table"
                        >
                          <thead className="table-light">
                            <tr>
                              <th>Type</th>
                              <th>Shots</th>
                              <th>Scored</th>
                              <th>Points</th>
                              <th>xPoints</th>
                              <th>Efficiency</th>
                              <th>Conversion</th>
                            </tr>
                          </thead>
                          <tbody>
                            {rows.map((r) => (
                              <tr key={r.type}>
                                <td>{r.type}</td>
                                <td>{r.num}</td>
                                <td>{r.scored}</td>
                                <td>{r.points}</td>
                                <td>{r.xPoints}</td>
                                <td>{r.eff}</td>
                                <td>{r.conversionRate}</td>
                              </tr>
                            ))}
                          </tbody>
                        </Table>
                      </Col>
                    );
                  })}
                </Row>
              </Card.Body>
            )}
          </Card>

          {/* Player Shot Efficiency */}
          <Card className="border-warning mb-4" ref={playerCardRef}>
            <Card.Header
              className="bg-warning text-dark d-flex justify-content-between align-items-center"
              style={{ cursor: "pointer" }}
              onClick={() => setOpenPlayer((o) => !o)}
            >
              <strong>Player Shot Efficiency</strong>
              {openPlayer ? <FaChevronUp /> : <FaChevronDown />}
            </Card.Header>
            {openPlayer && (
              <Card.Body className="bg-dark text-white">
                <Row>
                  {["home", "away"].map((side, i) => {
                    const rows = sortedPlayerRows(playerTables[side]);
                    return (
                      <Col key={i}>
                        <h6>
                          {side === "home"
                            ? selectedGame.HomeTeam
                            : selectedGame.AwayTeam}
                        </h6>
                        <Table
                          striped
                          bordered
                          hover
                          size="sm"
                          className="app-table"
                        >
                          <thead className="table-light">
                            <tr>
                              <th onClick={() => handlePlayerSort("player")}>
                                Player{" "}
                                {playerSortCol === "player" ? (
                                  playerSortAsc ? (
                                    <FaSortUp />
                                  ) : (
                                    <FaSortDown />
                                  )
                                ) : null}
                              </th>
                              <th onClick={() => handlePlayerSort("category")}>
                                Type{" "}
                                {playerSortCol === "category" ? (
                                  playerSortAsc ? (
                                    <FaSortUp />
                                  ) : (
                                    <FaSortDown />
                                  )
                                ) : null}
                              </th>
                              <th onClick={() => handlePlayerSort("num")}>
                                Shots{" "}
                                {playerSortCol === "num" ? (
                                  playerSortAsc ? (
                                    <FaSortUp />
                                  ) : (
                                    <FaSortDown />
                                  )
                                ) : null}
                              </th>
                              <th onClick={() => handlePlayerSort("scored")}>
                                Scored{" "}
                                {playerSortCol === "scored" ? (
                                  playerSortAsc ? (
                                    <FaSortUp />
                                  ) : (
                                    <FaSortDown />
                                  )
                                ) : null}
                              </th>
                              <th onClick={() => handlePlayerSort("points")}>
                                Points{" "}
                                {playerSortCol === "points" ? (
                                  playerSortAsc ? (
                                    <FaSortUp />
                                  ) : (
                                    <FaSortDown />
                                  )
                                ) : null}
                              </th>
                              <th onClick={() => handlePlayerSort("xPoints")}>
                                xPoints{" "}
                                {playerSortCol === "xPoints" ? (
                                  playerSortAsc ? (
                                    <FaSortUp />
                                  ) : (
                                    <FaSortDown />
                                  )
                                ) : null}
                              </th>
                              <th onClick={() => handlePlayerSort("eff")}>
                                Efficiency{" "}
                                {playerSortCol === "eff" ? (
                                  playerSortAsc ? (
                                    <FaSortUp />
                                  ) : (
                                    <FaSortDown />
                                  )
                                ) : null}
                              </th>
                              <th onClick={() => handlePlayerSort("conversionRate")}>
                                Conversion{" "}
                                {playerSortCol === "conversionRate" ? (
                                  playerSortAsc ? (
                                    <FaSortUp />
                                  ) : (
                                    <FaSortDown />
                                  )
                                ) : null}
                              </th>
                            </tr>
                          </thead>
                          <tbody>
                            {rows.map((r) => (
                              <tr key={r.key}>
                                <td>{r.player}</td>
                                <td>{r.category}</td>
                                <td>{r.num}</td>
                                <td>{r.scored}</td>
                                <td>{r.points}</td>
                                <td>{r.xPoints}</td>
                                <td>{r.eff}</td>
                                <td>{r.conversionRate}</td>
                              </tr>
                            ))}
                          </tbody>
                        </Table>
                      </Col>
                    );
                  })}
                </Row>
              </Card.Body>
            )}
          </Card>

          {/* Shot Map */}
          <Card className="border-warning mb-4" ref={mapCardRef}>
            <Card.Header
              className="bg-warning text-dark d-flex justify-content-between align-items-center"
              style={{ cursor: "pointer" }}
              onClick={() => setOpenMap((o) => !o)}
            >
              <strong>Shot Map</strong>
              {openMap ? <FaChevronUp /> : <FaChevronDown />}
            </Card.Header>
            {openMap && (
              <Card.Body className="bg-dark text-white">
                <ShotMapWithFilters
                  shotsData={normalizedShotsData}
                  game={selectedGame}
                />
              </Card.Body>
            )}
          </Card>
        </>
      )}
      {showTutorial && (
        <>
          {tutorialRect ? (
            <>
              <div
                onClick={closeTutorial}
                style={{
                  position: "fixed",
                  top: 0,
                  left: 0,
                  width: "100vw",
                  height: tutorialRect.top,
                  background: "rgba(8, 12, 18, 0.72)",
                  zIndex: 1060,
                }}
              />
              <div
                onClick={closeTutorial}
                style={{
                  position: "fixed",
                  top: tutorialRect.top,
                  left: 0,
                  width: tutorialRect.left,
                  height: tutorialRect.height,
                  background: "rgba(8, 12, 18, 0.72)",
                  zIndex: 1060,
                }}
              />
              <div
                onClick={closeTutorial}
                style={{
                  position: "fixed",
                  top: tutorialRect.top,
                  left: tutorialRect.left + tutorialRect.width,
                  width: `calc(100vw - ${tutorialRect.left + tutorialRect.width}px)`,
                  height: tutorialRect.height,
                  background: "rgba(8, 12, 18, 0.72)",
                  zIndex: 1060,
                }}
              />
              <div
                onClick={closeTutorial}
                style={{
                  position: "fixed",
                  top: tutorialRect.top + tutorialRect.height,
                  left: 0,
                  width: "100vw",
                  height: `calc(100vh - ${tutorialRect.top + tutorialRect.height}px)`,
                  background: "rgba(8, 12, 18, 0.72)",
                  zIndex: 1060,
                }}
              />
              <div
                style={{
                  position: "fixed",
                  top: tutorialRect.top,
                  left: tutorialRect.left,
                  width: tutorialRect.width,
                  height: tutorialRect.height,
                  border: "2px solid #f59e0b",
                  borderRadius: 12,
                  pointerEvents: "none",
                  zIndex: 1061,
                  transition:
                    "top 160ms ease, left 160ms ease, width 160ms ease, height 160ms ease",
                }}
              />
              <Card className="border-warning" style={getTutorialCardStyle()}>
                <Card.Header className="bg-warning text-dark d-flex justify-content-between align-items-center">
                  <strong>
                    {tutorialStepIndex + 1} / {tutorialSteps.length}{" "}
                    {tutorialSteps[tutorialStepIndex].title}
                  </strong>
                  <Button
                    variant="link"
                    className="text-dark p-0 text-decoration-none fw-semibold"
                    onClick={closeTutorial}
                  >
                    Close
                  </Button>
                </Card.Header>
                <Card.Body className="bg-dark text-white">
                  <p className="mb-3">
                    {tutorialSteps[tutorialStepIndex].body}
                  </p>
                  <div className="d-flex justify-content-between gap-2">
                    <Button
                      variant="outline-light"
                      size="sm"
                      disabled={tutorialStepIndex === 0}
                      onClick={prevTutorialStep}
                    >
                      Back
                    </Button>
                    <Button
                      variant="warning"
                      size="sm"
                      className="text-dark fw-semibold"
                      onClick={nextTutorialStep}
                    >
                      {tutorialStepIndex === tutorialSteps.length - 1
                        ? "Finish"
                        : "Next"}
                    </Button>
                  </div>
                </Card.Body>
              </Card>
            </>
          ) : (
            <div
              onClick={closeTutorial}
              style={{
                position: "fixed",
                inset: 0,
                background: "rgba(8, 12, 18, 0.72)",
                zIndex: 1060,
              }}
            />
          )}
        </>
      )}
    </Container>
  );
};

export default ShotAnalysisScreen;
